import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, RotateCcw, Search } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/types/database.types";

type Plan = Database["public"]["Tables"]["plans"]["Row"] & {
  features: Database["public"]["Tables"]["plan_features"]["Row"];
};

const PlanDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [planData, setPlanData] = useState<Plan | null>(null);
  const [originalPlanData, setOriginalPlanData] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const fetchPlanData = async () => {
      if (!id) {
        setError("Plan ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from("plans")
          .select(
            `
            *,
            plan_features!inner(*)
          `,
          )
          .eq("id", id)
          .single();

        if (fetchError) {
          setError(fetchError.message);
        } else if (data) {
          // Transform the data to match our Plan type
          const planWithFeatures: Plan = {
            ...data,
            features: data.plan_features[0], // Since we have inner join, should have exactly one
          };
          setPlanData(planWithFeatures);
          setOriginalPlanData(JSON.parse(JSON.stringify(planWithFeatures))); // Deep clone
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load plan");
      } finally {
        setLoading(false);
      }
    };

    fetchPlanData();
  }, [id]);

  const handleFeatureChange = (feature: string, value: any) => {
    if (!planData) return;
    setPlanData({
      ...planData,
      features: {
        ...planData.features,
        [feature]: value,
      },
    });
  };

  const handleBasicInfoChange = (field: string, value: any) => {
    if (!planData) return;
    setPlanData({
      ...planData,
      [field]: value,
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleResetChanges = () => {
    if (originalPlanData) {
      setPlanData(JSON.parse(JSON.stringify(originalPlanData))); // Deep clone to reset
      toast({
        title: "Changes Reset",
        description: "All changes have been reverted to original values.",
      });
    }
  };

  const validatePlanData = () => {
    if (!planData) return "Plan data is missing";

    if (!planData.name || planData.name.trim().length === 0) {
      return "Plan name is required";
    }

    if (planData.monthly_price < 0) {
      return "Monthly price cannot be negative";
    }

    return null;
  };

  const handleSaveChanges = async () => {
    if (!planData || !id) return;

    // Validate form data
    const validationError = validatePlanData();
    if (validationError) {
      setError(validationError);
      toast({
        title: "Validation Error",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Debug logging
      console.log("🔍 Saving plan data:", {
        planId: id,
        max_active_agents: planData.features.max_active_agents,
        features: planData.features,
      });

      // Use the admin edge function to update both plan and features
      const { data, error } = await supabase.functions.invoke(
        "admin-update-plan",
        {
          body: {
            planId: id,
            planUpdates: {
              name: planData.name,
              description: planData.description,
              monthly_price: planData.monthly_price,
              is_active: planData.is_active,
            },
            featuresUpdates: planData.features,
          },
        },
      );

      if (error) {
        throw new Error(`Failed to save changes: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error("Failed to save changes: Invalid response");
      }

      // Update both current and original data
      setPlanData(data.data);
      setOriginalPlanData(JSON.parse(JSON.stringify(data.data)));

      toast({
        title: "Plan Updated",
        description: "Plan changes have been saved successfully.",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save changes";
      setError(errorMessage);
      toast({
        title: "Save Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!originalPlanData || !planData) return false;
    return JSON.stringify(originalPlanData) !== JSON.stringify(planData);
  };

  // Search functionality
  const searchableFields = [
    { id: "plan-name", label: "Plan Name", section: "Basic Information" },
    { id: "description", label: "Description", section: "Basic Information" },
    { id: "is-active", label: "Plan is Active", section: "Basic Information" },
    {
      id: "max-agents",
      label: "Maximum Active Voice Agents",
      section: "Usage Limits",
    },
    {
      id: "max-ai-assistants",
      label: "AI Assistant Instances",
      section: "Usage Limits",
    },
    {
      id: "max-ai-generations",
      label: "AI Generations per Month",
      section: "Usage Limits",
    },
    {
      id: "max-voice-minutes",
      label: "Voice Minutes per Month",
      section: "Usage Limits",
    },
    {
      id: "max-voice-calls",
      label: "Voice Calls per Month",
      section: "Usage Limits",
    },
    {
      id: "voice-overage-cost",
      label: "Voice Overage Cost",
      section: "Usage Limits",
    },
    { id: "max-knowledge", label: "Knowledge Items", section: "Storage" },
    { id: "max-files", label: "Maximum Files", section: "Storage" },
    { id: "max-storage", label: "Storage (MB)", section: "Storage" },
    {
      id: "max-team-members",
      label: "Maximum Team Members",
      section: "Team & API",
    },
    {
      id: "max-api-calls",
      label: "API Calls per Month",
      section: "Team & API",
    },
    { id: "max-webhooks", label: "Webhook Endpoints", section: "Team & API" },
    // Agent Features
    {
      id: "voice-agents-toggle",
      label: "Voice Agents",
      section: "Features",
      searchValue: "Voice Agents agents voice",
    },
    {
      id: "web-search-toggle",
      label: "Web Search",
      section: "Features",
      searchValue: "Web Search search browse",
    },
    {
      id: "file-collection-toggle",
      label: "File Collection",
      section: "Features",
      searchValue: "File Collection collect files upload",
    },
    {
      id: "phone-collection-toggle",
      label: "Phone Collection",
      section: "Features",
      searchValue: "Phone Collection collect phone number",
    },
    {
      id: "human-escalation-toggle",
      label: "Human Escalation",
      section: "Features",
      searchValue: "Human Escalation escalate operator",
    },
    {
      id: "multi-language-toggle",
      label: "Multi-Language Support",
      section: "Features",
      searchValue: "Multi-Language Support language multilingual translate",
    },
    // Integration Features
    {
      id: "api-access-toggle",
      label: "API Access",
      section: "Features",
      searchValue: "API Access api integration",
    },
    {
      id: "team-collaboration-toggle",
      label: "Team Collaboration",
      section: "Features",
      searchValue: "Team Collaboration team collaborate members",
    },
    // Branding Features
    {
      id: "remove-powered-by-toggle",
      label: "Remove Powered By",
      section: "Features",
      searchValue: "Remove Powered By branding powered firmagent",
    },
    {
      id: "custom-domain-toggle",
      label: "Custom Domain",
      section: "Features",
      searchValue: "Custom Domain domain url",
    },
    {
      id: "custom-onboarding-toggle",
      label: "Custom Onboarding",
      section: "Features",
      searchValue: "Custom Onboarding onboarding personalized",
    },
  ];

  const scrollToField = useCallback((fieldId: string) => {
    const element = document.getElementById(fieldId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      element.focus();
      setCommandOpen(false);
    }
  }, []);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <SuperAdminLayout
          title="Loading Plan..."
          description="Loading plan details"
        >
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  if (error || !planData) {
    return (
      <MainLayout>
        <SuperAdminLayout
          title="Plan Not Found"
          description="Unable to load plan details"
        >
          <Alert variant="destructive">
            <AlertDescription>{error || "Plan not found"}</AlertDescription>
          </Alert>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SuperAdminLayout
        title="Plan Editor"
        description="Configure plan features and pricing"
        actions={
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/super-admin/plans">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Plans
              </Link>
            </Button>
            <Button variant="ghost" onClick={() => setCommandOpen(true)}>
              <Search className="h-4 w-4 mr-2" />
              Search
              <CommandShortcut>⌘K</CommandShortcut>
            </Button>
            <Button
              variant="ghost"
              onClick={handleResetChanges}
              disabled={!hasChanges() || saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Changes
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={!hasChanges() || saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        }
      >
        <div className="flex gap-8 relative pt-6">
          {/* Left: Main Form */}
          <div className="flex-1 max-w-4xl">
            <div className="space-y-8">
              {/* Basic Information Section */}
              <div className="space-y-6">
                <div className="pb-2 border-b">
                  <h2 className="text-lg font-semibold">Basic Information</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Core plan details and Stripe integration settings
                  </p>
                </div>

                <Alert>
                  <AlertDescription>
                    <strong>Note:</strong> Stripe-related fields are read-only.
                    Price changes must be made in Stripe first.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="plan-name">Plan Name</Label>
                    <Input
                      id="plan-name"
                      value={planData.name}
                      onChange={(e) =>
                        handleBasicInfoChange("name", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly-price">Monthly Price ($)</Label>
                    <Input
                      id="monthly-price"
                      type="number"
                      value={planData.monthly_price}
                      readOnly
                      className="bg-muted cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={planData.description}
                    onChange={(e) =>
                      handleBasicInfoChange("description", e.target.value)
                    }
                    placeholder="Describe what this plan offers..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="stripe-product">Stripe Product ID</Label>
                    <Input
                      id="stripe-product"
                      value={planData.stripe_product_id}
                      readOnly
                      className="bg-muted cursor-not-allowed font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stripe-price">Stripe Price ID</Label>
                    <Input
                      id="stripe-price"
                      value={planData.stripe_price_id}
                      readOnly
                      className="bg-muted cursor-not-allowed font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-active"
                    checked={planData.is_active}
                    onCheckedChange={(checked) =>
                      handleBasicInfoChange("is_active", checked)
                    }
                  />
                  <Label htmlFor="is-active">Plan is active</Label>
                </div>
              </div>

              {/* Usage Limits Section */}
              <div className="space-y-6">
                <div className="pb-2 border-b">
                  <h2 className="text-lg font-semibold">Usage Limits</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set monthly usage limits for different features
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Agents */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-agents">
                        Maximum Active Voice Agents
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-agents"
                            checked={!planData.features?.max_active_agents}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange("max_active_agents", null);
                              } else {
                                handleFeatureChange("max_active_agents", 1);
                              }
                            }}
                          />
                          <Label htmlFor="unlimited-agents" className="text-sm">
                            Unlimited voice agents
                          </Label>
                        </div>
                        {planData.features?.max_active_agents && (
                          <Input
                            id="max-agents"
                            type="number"
                            min="1"
                            value={planData.features.max_active_agents}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_active_agents",
                                parseInt(e.target.value) || 1,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_active_agents
                          ? `Limited to ${planData.features.max_active_agents} voice agents`
                          : "No limit on voice agents"}
                      </p>
                    </div>
                    <div className="space-y-2 opacity-50">
                      <Label
                        htmlFor="max-conversations"
                        className="text-muted-foreground"
                      >
                        Conversations per Month (Chat Agents - Sunset)
                      </Label>
                      <Input
                        id="max-conversations"
                        type="number"
                        value={
                          planData.features?.max_conversations_per_month || 0
                        }
                        disabled
                        className="bg-muted cursor-not-allowed"
                      />
                      <p className="text-sm text-muted-foreground">
                        Chat agents are being sunset - this field is disabled
                      </p>
                    </div>
                  </div>

                  {/* AI Assistants */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-ai-assistants">
                        AI Assistant Instances
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-ai-assistants"
                            checked={
                              !planData.features?.max_ai_assistant_instances
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_ai_assistant_instances",
                                  null,
                                );
                              } else {
                                handleFeatureChange(
                                  "max_ai_assistant_instances",
                                  1,
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-ai-assistants"
                            className="text-sm"
                          >
                            Unlimited AI assistants
                          </Label>
                        </div>
                        {planData.features?.max_ai_assistant_instances && (
                          <Input
                            id="max-ai-assistants"
                            type="number"
                            min="1"
                            value={planData.features.max_ai_assistant_instances}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_ai_assistant_instances",
                                parseInt(e.target.value) || 1,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_ai_assistant_instances
                          ? `Limited to ${planData.features.max_ai_assistant_instances} AI assistants`
                          : "No limit on AI assistants"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-ai-generations">
                        AI Generations per Month
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-ai-generations"
                            checked={
                              !planData.features?.max_ai_generations_per_month
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_ai_generations_per_month",
                                  null,
                                );
                              } else {
                                handleFeatureChange(
                                  "max_ai_generations_per_month",
                                  1000,
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-ai-generations"
                            className="text-sm"
                          >
                            Unlimited AI generations
                          </Label>
                        </div>
                        {planData.features?.max_ai_generations_per_month && (
                          <Input
                            id="max-ai-generations"
                            type="number"
                            min="1"
                            value={
                              planData.features.max_ai_generations_per_month
                            }
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_ai_generations_per_month",
                                parseInt(e.target.value) || 1000,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_ai_generations_per_month
                          ? `Limited to ${planData.features.max_ai_generations_per_month} generations per month`
                          : "No limit on AI generations"}
                      </p>
                    </div>
                  </div>

                  {/* Voice */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-voice-minutes">
                        Voice Minutes per Month
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-voice-minutes"
                            checked={
                              !planData.features?.max_voice_minutes_per_month
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_voice_minutes_per_month",
                                  null,
                                );
                              } else {
                                handleFeatureChange(
                                  "max_voice_minutes_per_month",
                                  60,
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-voice-minutes"
                            className="text-sm"
                          >
                            Unlimited voice minutes
                          </Label>
                        </div>
                        {planData.features?.max_voice_minutes_per_month && (
                          <Input
                            id="max-voice-minutes"
                            type="number"
                            min="1"
                            value={
                              planData.features.max_voice_minutes_per_month
                            }
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_voice_minutes_per_month",
                                parseInt(e.target.value) || 60,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_voice_minutes_per_month
                          ? `Limited to ${planData.features.max_voice_minutes_per_month} minutes per month`
                          : "No limit on voice minutes"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-voice-calls">
                        Voice Calls per Month
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-voice-calls"
                            checked={
                              !planData.features?.max_voice_calls_per_month
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_voice_calls_per_month",
                                  null,
                                );
                              } else {
                                handleFeatureChange(
                                  "max_voice_calls_per_month",
                                  100,
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-voice-calls"
                            className="text-sm"
                          >
                            Unlimited voice calls
                          </Label>
                        </div>
                        {planData.features?.max_voice_calls_per_month && (
                          <Input
                            id="max-voice-calls"
                            type="number"
                            min="1"
                            value={planData.features.max_voice_calls_per_month}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_voice_calls_per_month",
                                parseInt(e.target.value) || 100,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_voice_calls_per_month
                          ? `Limited to ${planData.features.max_voice_calls_per_month} calls per month`
                          : "No limit on voice calls"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="voice-overage-cost">
                        Voice Overage ($/min)
                      </Label>
                      <Input
                        id="voice-overage-cost"
                        type="number"
                        step="0.01"
                        value={
                          planData.features?.voice_overage_cost_per_minute || 0
                        }
                        onChange={(e) =>
                          handleFeatureChange(
                            "voice_overage_cost_per_minute",
                            parseFloat(e.target.value) || null,
                          )
                        }
                      />
                      <p className="text-sm text-muted-foreground">
                        Cost per minute over limit
                      </p>
                    </div>
                  </div>

                  {/* Storage */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-knowledge">Knowledge Items</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-knowledge"
                            checked={!planData.features?.max_knowledge_items}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_knowledge_items",
                                  null,
                                );
                              } else {
                                handleFeatureChange("max_knowledge_items", 10);
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-knowledge"
                            className="text-sm"
                          >
                            Unlimited knowledge items
                          </Label>
                        </div>
                        {planData.features?.max_knowledge_items && (
                          <Input
                            id="max-knowledge"
                            type="number"
                            min="1"
                            value={planData.features.max_knowledge_items}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_knowledge_items",
                                parseInt(e.target.value) || 10,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_knowledge_items
                          ? `Limited to ${planData.features.max_knowledge_items} knowledge items`
                          : "No limit on knowledge items"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-files">Maximum Files</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-files"
                            checked={!planData.features?.max_files}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange("max_files", null);
                              } else {
                                handleFeatureChange("max_files", 5);
                              }
                            }}
                          />
                          <Label htmlFor="unlimited-files" className="text-sm">
                            Unlimited files
                          </Label>
                        </div>
                        {planData.features?.max_files && (
                          <Input
                            id="max-files"
                            type="number"
                            min="1"
                            value={planData.features.max_files}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_files",
                                parseInt(e.target.value) || 5,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_files
                          ? `Limited to ${planData.features.max_files} files`
                          : "No limit on files"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-storage">Storage (MB)</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-storage"
                            checked={!planData.features?.max_storage_mb}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange("max_storage_mb", null);
                              } else {
                                handleFeatureChange("max_storage_mb", 100);
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-storage"
                            className="text-sm"
                          >
                            Unlimited storage
                          </Label>
                        </div>
                        {planData.features?.max_storage_mb && (
                          <Input
                            id="max-storage"
                            type="number"
                            min="1"
                            value={planData.features.max_storage_mb}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_storage_mb",
                                parseInt(e.target.value) || 100,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_storage_mb
                          ? `Limited to ${planData.features.max_storage_mb} MB`
                          : "No storage limit"}
                      </p>
                    </div>
                  </div>

                  {/* Team & API */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-team-members">
                        Maximum Team Members
                      </Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-team-members"
                            checked={!planData.features?.max_team_members}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange("max_team_members", null);
                              } else {
                                handleFeatureChange("max_team_members", 3);
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-team-members"
                            className="text-sm"
                          >
                            Unlimited team members
                          </Label>
                        </div>
                        {planData.features?.max_team_members && (
                          <Input
                            id="max-team-members"
                            type="number"
                            min="1"
                            value={planData.features.max_team_members}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_team_members",
                                parseInt(e.target.value) || 3,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_team_members
                          ? `Limited to ${planData.features.max_team_members} team members`
                          : "No limit on team members"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-api-calls">API Calls per Month</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-api-calls"
                            checked={
                              !planData.features?.max_api_calls_per_month
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_api_calls_per_month",
                                  null,
                                );
                              } else {
                                handleFeatureChange(
                                  "max_api_calls_per_month",
                                  1000,
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-api-calls"
                            className="text-sm"
                          >
                            Unlimited API calls
                          </Label>
                        </div>
                        {planData.features?.max_api_calls_per_month && (
                          <Input
                            id="max-api-calls"
                            type="number"
                            min="1"
                            value={planData.features.max_api_calls_per_month}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_api_calls_per_month",
                                parseInt(e.target.value) || 1000,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_api_calls_per_month
                          ? `Limited to ${planData.features.max_api_calls_per_month} API calls per month`
                          : "No limit on API calls"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-webhooks">Webhook Endpoints</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="unlimited-webhooks"
                            checked={!planData.features?.max_webhook_endpoints}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFeatureChange(
                                  "max_webhook_endpoints",
                                  null,
                                );
                              } else {
                                handleFeatureChange("max_webhook_endpoints", 5);
                              }
                            }}
                          />
                          <Label
                            htmlFor="unlimited-webhooks"
                            className="text-sm"
                          >
                            Unlimited webhook endpoints
                          </Label>
                        </div>
                        {planData.features?.max_webhook_endpoints && (
                          <Input
                            id="max-webhooks"
                            type="number"
                            min="1"
                            value={planData.features.max_webhook_endpoints}
                            onChange={(e) =>
                              handleFeatureChange(
                                "max_webhook_endpoints",
                                parseInt(e.target.value) || 5,
                              )
                            }
                          />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {planData.features?.max_webhook_endpoints
                          ? `Limited to ${planData.features.max_webhook_endpoints} webhook endpoints`
                          : "No limit on webhook endpoints"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Section */}
              <div className="space-y-6">
                <div className="pb-2 border-b">
                  <h2 className="text-lg font-semibold">Features</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enable or disable specific functionality for this plan
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Agent Capabilities */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground">
                      Agent Capabilities
                    </h4>
                    <div className="space-y-4">
                      {/* Master Agents Toggle */}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">Voice Agents</Label>
                          <p className="text-sm text-muted-foreground">
                            Enable voice agent functionality (chat agents are
                            sunset)
                          </p>
                        </div>
                        <Switch
                          id="voice-agents-toggle"
                          checked={planData.features?.has_agents || false}
                          onCheckedChange={(checked) =>
                            handleFeatureChange("has_agents", checked)
                          }
                        />
                      </div>

                      <Separator />

                      {/* Agent Sub-features - disabled when agents are off */}
                      <div
                        className={`space-y-4 ${!planData.features?.has_agents ? "opacity-50" : ""}`}
                      >
                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label
                              className={`font-medium ${!planData.features?.has_agents ? "text-muted-foreground" : ""}`}
                            >
                              Web Search
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Allow agents to search the web
                            </p>
                          </div>
                          <Switch
                            id="web-search-toggle"
                            checked={planData.features?.can_web_search || false}
                            disabled={!planData.features?.has_agents}
                            onCheckedChange={(checked) =>
                              handleFeatureChange("can_web_search", checked)
                            }
                          />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label
                              className={`font-medium ${!planData.features?.has_agents ? "text-muted-foreground" : ""}`}
                            >
                              File Collection
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Allow agents to collect files from users
                            </p>
                          </div>
                          <Switch
                            id="file-collection-toggle"
                            checked={
                              planData.features?.can_collect_files || false
                            }
                            disabled={!planData.features?.has_agents}
                            onCheckedChange={(checked) =>
                              handleFeatureChange("can_collect_files", checked)
                            }
                          />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label
                              className={`font-medium ${!planData.features?.has_agents ? "text-muted-foreground" : ""}`}
                            >
                              Phone Collection
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Allow agents to collect phone numbers
                            </p>
                          </div>
                          <Switch
                            id="phone-collection-toggle"
                            checked={
                              planData.features?.can_collect_phone || false
                            }
                            disabled={!planData.features?.has_agents}
                            onCheckedChange={(checked) =>
                              handleFeatureChange("can_collect_phone", checked)
                            }
                          />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label
                              className={`font-medium ${!planData.features?.has_agents ? "text-muted-foreground" : ""}`}
                            >
                              Human Escalation
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Allow escalation to human operators
                            </p>
                          </div>
                          <Switch
                            id="human-escalation-toggle"
                            checked={
                              planData.features?.can_human_escalation || false
                            }
                            disabled={!planData.features?.has_agents}
                            onCheckedChange={(checked) =>
                              handleFeatureChange(
                                "can_human_escalation",
                                checked,
                              )
                            }
                          />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label
                              className={`font-medium ${!planData.features?.has_agents ? "text-muted-foreground" : ""}`}
                            >
                              Multi-Language Support
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Support for multiple languages
                            </p>
                          </div>
                          <Switch
                            id="multi-language-toggle"
                            checked={
                              planData.features?.can_multi_language || false
                            }
                            disabled={!planData.features?.has_agents}
                            onCheckedChange={(checked) =>
                              handleFeatureChange("can_multi_language", checked)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Integrations & API */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground">
                      Integrations & API
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">API Access</Label>
                          <p className="text-sm text-muted-foreground">
                            Enable API access for integrations
                          </p>
                        </div>
                        <Switch
                          id="api-access-toggle"
                          checked={
                            planData.features?.api_access_enabled || false
                          }
                          onCheckedChange={(checked) =>
                            handleFeatureChange("api_access_enabled", checked)
                          }
                        />
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">
                            Team Collaboration
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Enable team member collaboration features
                          </p>
                        </div>
                        <Switch
                          id="team-collaboration-toggle"
                          checked={
                            planData.features?.can_team_collaboration || false
                          }
                          onCheckedChange={(checked) =>
                            handleFeatureChange(
                              "can_team_collaboration",
                              checked,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Branding & Customization */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground">
                      Branding & Customization
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">
                            Remove Powered By
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Remove "Powered by FirmAgent" branding
                          </p>
                        </div>
                        <Switch
                          id="remove-powered-by-toggle"
                          checked={
                            planData.features?.can_remove_powered_by || false
                          }
                          onCheckedChange={(checked) =>
                            handleFeatureChange(
                              "can_remove_powered_by",
                              checked,
                            )
                          }
                        />
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">Custom Domain</Label>
                          <p className="text-sm text-muted-foreground">
                            Allow custom domain for chat widget
                          </p>
                        </div>
                        <Switch
                          id="custom-domain-toggle"
                          checked={
                            planData.features?.can_custom_domain || false
                          }
                          onCheckedChange={(checked) =>
                            handleFeatureChange("can_custom_domain", checked)
                          }
                        />
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label className="font-medium">
                            Custom Onboarding
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Personalized onboarding assistance
                          </p>
                        </div>
                        <Switch
                          id="custom-onboarding-toggle"
                          checked={
                            planData.features?.has_custom_onboarding || false
                          }
                          onCheckedChange={(checked) =>
                            handleFeatureChange(
                              "has_custom_onboarding",
                              checked,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Rail - Sticky */}
          <div className="w-80 sticky top-8 h-fit">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{planData.name}</CardTitle>
                  <Badge variant={planData.is_active ? "default" : "secondary"}>
                    {planData.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-2xl font-bold">
                    {formatPrice(planData.monthly_price)}
                    <span className="text-sm font-normal text-muted-foreground">
                      /month
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Managed by Stripe
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Quick Actions</h4>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start"
                      onClick={handleResetChanges}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Changes
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Unsaved Changes</h4>
                  {hasChanges() ? (
                    <p className="text-sm text-amber-600">
                      You have unsaved changes
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No unsaved changes
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Command Palette */}
        <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
          <CommandInput placeholder="Search plan settings..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading="Basic Information">
              {searchableFields
                .filter((field) => field.section === "Basic Information")
                .map((field) => (
                  <CommandItem
                    key={field.id}
                    value={field.searchValue || field.label}
                    onSelect={() => scrollToField(field.id)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Usage Limits">
              {searchableFields
                .filter((field) => field.section === "Usage Limits")
                .map((field) => (
                  <CommandItem
                    key={field.id}
                    value={field.searchValue || field.label}
                    onSelect={() => scrollToField(field.id)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Storage">
              {searchableFields
                .filter((field) => field.section === "Storage")
                .map((field) => (
                  <CommandItem
                    key={field.id}
                    value={field.searchValue || field.label}
                    onSelect={() => scrollToField(field.id)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Team & API">
              {searchableFields
                .filter((field) => field.section === "Team & API")
                .map((field) => (
                  <CommandItem
                    key={field.id}
                    value={field.searchValue || field.label}
                    onSelect={() => scrollToField(field.id)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Features">
              {searchableFields
                .filter((field) => field.section === "Features")
                .map((field) => (
                  <CommandItem
                    key={field.id}
                    value={field.searchValue || field.label}
                    onSelect={() => scrollToField(field.id)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </CommandDialog>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default PlanDetailPage;
